import { useState } from 'react';
import { FiChevronDown, FiMessageCircle, FiSearch } from 'react-icons/fi';
import { Area, Calor } from '../Grafico';
import { corDaSerie, reaisInteiros } from '../visual';
import { BotaoDoRodape, PedeAcao } from './Alertas';
import { Aviso, Box, Button, Cartao, CartaoComTitulo, Flex, Grade, Kpi, LinhaDoPeriodo, Variacao } from './base';
import { BarraDividida, Colunas, Medidor } from './Visuais';
import { curto, nomeDoMes, oAnterior, plural, quantidade, reais, reaisCurtos } from './formato';
import { PedidoDeInvestigacao } from './Gaveta';
import { MapaDasRegioes } from './MapaDasRegioes';
import { TabelasDaAnalise } from './Tabela';
import { Alerta, Analise, Tabela } from './tipos';

// As abas "Este mês" e "Mês passado" (antes, a semanal e a de 28 dias, que
// usam esta mesma tela): a mesma forma, cada uma
// com os períodos, os números, os gráficos, os alertas e as tabelas DELA. O
// percentual de uma nunca aparece na outra. Hoje fica de fora das duas.
//
// O desenho segue o de um painel de métricas: uma faixa de indicadores
// iguais; a venda dia a dia ao lado do que pede ação; embaixo, três cartões
// (a venda por família, a parte de cada vendedor e os maiores clientes). O
// mapa de horários e as tabelas ficam em "Ver detalhamento"; os avisos sobre os
// dados, em "Sobre os dados". As partes vêm prontas do MCP (`composicao`).

// Um botão que abre e fecha uma parte recolhida.
function Recolhido({ rotulo, children, inicialmenteAberto = false }: { rotulo: string; children: React.ReactNode; inicialmenteAberto?: boolean }) {
  const [aberto, setAberto] = useState(inicialmenteAberto);
  return (
    <Box>
      <Button
        size="sm"
        variant="ghost"
        h="36px"
        px="10px"
        fontSize="13px"
        fontWeight={600}
        color="var(--pa-tinta-2)"
        rightIcon={
          <Box as="span" transition="transform .15s" transform={aberto ? 'rotate(180deg)' : undefined}>
            <FiChevronDown aria-hidden="true" />
          </Box>
        }
        aria-expanded={aberto}
        onClick={() => setAberto(!aberto)}
      >
        {rotulo}
      </Button>
      {aberto && <Box mt="12px">{children}</Box>}
    </Box>
  );
}

const capital = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

const daTabela = (tabelas: Tabela[] | undefined, nome: string) => tabelas?.find((t) => t.nome === nome);

// A tabela do cartão "Maiores clientes": os 5 primeiros e, em "Ver todos", o
// resto, numa caixa que rola (a empresa inteira passa de 200 clientes no mês).
// A região ("009 · DF") e o estado vêm do cadastro do cliente; o MCP antigo
// não os manda, e a coluna some.
const CLIENTES_DE_INICIO = 5;

function ListaDeClientes({ linhas }: { linhas: Tabela['linhas'] }) {
  const [todos, setTodos] = useState(false);
  const comRegiao = linhas.some((l) => l.regiao);
  const comUf = linhas.some((l) => l.uf);
  const visiveis = todos ? linhas : linhas.slice(0, CLIENTES_DE_INICIO);
  return (
    <>
      <Box mt="12px" maxH={todos ? '420px' : undefined} overflowY={todos ? 'auto' : undefined}>
        <Box as="table" w="100%" fontSize="13px" sx={{ borderCollapse: 'collapse' }}>
          <Box as="thead" color="var(--pa-tinta-3)" fontSize="12px" position={todos ? 'sticky' : undefined} top={0} bg="var(--pa-superficie)">
            <tr>
              <Box as="th" textAlign="left" fontWeight={500} pb="6px">
                Cliente
              </Box>
              {comRegiao && (
                <Box as="th" textAlign="left" fontWeight={500} pb="6px" pl="8px">
                  Região
                </Box>
              )}
              {comUf && (
                <Box as="th" textAlign="left" fontWeight={500} pb="6px" pl="8px">
                  UF
                </Box>
              )}
              <Box as="th" textAlign="right" fontWeight={500} pb="6px">
                Pedidos
              </Box>
              <Box as="th" textAlign="right" fontWeight={500} pb="6px">
                Venda
              </Box>
            </tr>
          </Box>
          <tbody>
            {visiveis.map((l, i) => (
              <Box as="tr" key={`${l.cliente}-${i}`} borderTop="1px dashed var(--pa-linha)">
                <Box as="td" py="7px" pr="8px" maxW="0" w={comRegiao ? '45%' : '60%'} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={String(l.cliente)}>
                  {String(l.cliente)}
                </Box>
                {comRegiao && (
                  <Box as="td" py="7px" pl="8px" maxW="0" w="22%" color="var(--pa-tinta-2)" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={l.regiao ? String(l.regiao) : undefined}>
                    {l.regiao ? String(l.regiao) : '—'}
                  </Box>
                )}
                {comUf && (
                  <Box as="td" py="7px" pl="8px" color="var(--pa-tinta-2)" whiteSpace="nowrap">
                    {l.uf ? String(l.uf) : '—'}
                  </Box>
                )}
                <Box as="td" py="7px" textAlign="right" color="var(--pa-tinta-2)">
                  {l.pedidos == null ? '—' : quantidade(Number(l.pedidos))}
                </Box>
                <Box as="td" py="7px" pl="8px" textAlign="right" fontWeight={600} whiteSpace="nowrap" title={reais(Number(l.venda) || 0)}>
                  {reaisCurtos(Number(l.venda) || 0)}
                </Box>
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
      {linhas.length > CLIENTES_DE_INICIO && (
        <Flex justify="center" mt="10px">
          <Button variant="ghost" size="sm" fontSize="13px" color="var(--pa-tinta-2)" aria-expanded={todos} onClick={() => setTodos(!todos)}>
            {todos ? 'Mostrar menos' : `Ver todos os ${linhas.length}`}
          </Button>
        </Flex>
      )}
    </>
  );
}

export function AbaComparacao({
  analise,
  cores,
  onAbrir,
  onInvestigar,
  onPerguntar,
  regioes = {},
  regiao = '',
  onEscolherRegiao,
}: {
  analise: Analise;
  cores: string[];
  onAbrir: (a: Alerta) => void;
  onInvestigar: (p: PedidoDeInvestigacao, titulo: string) => void;
  onPerguntar: (texto: string) => void;
  // O mapa: as regiões do filtro, a escolhida e como escolher outra.
  regioes?: Record<string, string>;
  regiao?: string;
  onEscolherRegiao?: (codigo: string) => void;
}) {
  const { atual, anterior } = analise.periodo;

  if (!analise.disponivel || !atual) {
    return (
      <Cartao px="20px" py="32px" textAlign="center">
        <Box as="h3" m={0} fontSize="16px" fontWeight={700}>
          Ainda sem histórico para esta análise
        </Box>
        <Box fontSize="14px" color="var(--pa-tinta-2)" mt="6px" maxW="520px" mx="auto">
          {analise.descricao} Nenhum número aparece até haver os dois períodos inteiros no sistema
          {analise.primeira_comparacao_em ? (
            <>
              : a primeira comparação será em <b>{analise.primeira_comparacao_em}</b>.
            </>
          ) : (
            '.'
          )}
        </Box>
        {analise.limitacoes.map((t) => (
          <Box key={t} fontSize="13px" color="var(--pa-tinta-3)" mt="8px">
            {t}
          </Box>
        ))}
      </Cartao>
    );
  }

  const ind = analise.indicadores!;
  const a = ind.atual;
  const v = ind.variacao || {};
  const comparavel = analise.comparavel && !!ind.anterior;
  const porDia = analise.graficos?.find((g) => g.tipo === 'linha');
  const calor = analise.graficos?.find((g) => g.tipo === 'mapa_de_calor');
  const familias = daTabela(analise.tabelas, 'grupos_de_produto');
  const clientes = daTabela(analise.tabelas, 'clientes');
  const composicao = analise.composicao;
  const pedido: PedidoDeInvestigacao = { analise: analise.analise, periodo: atual };
  const semComparacao = analise.primeira_comparacao_em
    ? `Ainda sem histórico para comparar: a primeira comparação será em ${analise.primeira_comparacao_em}. Até lá, só os números do período.`
    : analise.limitacoes.find((t) => /^Poucos pedidos/.test(t)) || 'Sem período anterior para comparar.';
  // Margem de parte da venda (pedido digitado no ERP chega sem ela) não é a
  // margem da operação: fica fora dos indicadores e vai para "Sobre os dados".
  const margemParcial = a.margem_sobre_percentual_da_venda != null;
  const mostraMargem = a.margem_percentual != null && !margemParcial;
  const sobre = [
    ...(margemParcial && a.margem_percentual != null
      ? [
          `Margem de ${quantidade(a.margem_percentual)}% só de ${quantidade(a.margem_sobre_percentual_da_venda!)}% da venda: pedido digitado no ERP chega sem margem, e o número fica fora dos indicadores.`,
        ]
      : []),
    ...analise.limitacoes,
  ];
  const arquivo = `${atual.inicio.replace(/\//g, '-')}_a_${atual.fim.replace(/\//g, '-')}`;
  const noAnterior = (texto: string) => (comparavel && anterior ? `${texto} ${oAnterior(analise.analise, anterior)}` : 'sem comparação');
  const queda = (v.venda_percentual || 0) < 0;
  const frase = !comparavel
    ? 'Sem período anterior para comparar.'
    : v.venda_percentual == null
      ? 'Sem venda no período anterior para comparar.'
      : v.venda_percentual === 0
        ? 'A venda ficou igual à do período anterior.'
        : `A venda${v.por_dia_util ? ' por dia útil' : ''} ${queda ? 'caiu' : 'subiu'} ${quantidade(Math.abs(v.venda_percentual))}%${
            v.venda_diferenca != null ? ` (${queda ? '−' : '+'} ${reaisCurtos(Math.abs(v.venda_diferenca))})` : ''
          } contra ${oAnterior(analise.analise, anterior!).replace(/^(em|de) /, '')}${
            v.por_dia_util
              ? ` (${plural(v.por_dia_util.dias_uteis_atual, 'dia útil', 'dias úteis')} contra ${v.por_dia_util.dias_uteis_anterior}, por feriado)`
              : ''
          }.`;
  const indicadores = 4 + (mostraMargem ? 1 : 0);
  const cartoesDeBaixo = [familias, composicao?.vendedores, clientes].filter(Boolean).length;
  const largura = { lg: `span ${cartoesDeBaixo ? 12 / cartoesDeBaixo : 12}` };

  return (
    <Flex direction="column" sx={{ gap: '20px' }}>
      <Flex direction="column" sx={{ gap: '10px' }}>
        <LinhaDoPeriodo>
          {analise.analise === 'mes_passado' ? (
            <>
              <b>{capital(nomeDoMes(atual))}</b> inteiro ({curto(atual)})
              {anterior ? (
                <>
                  {' '}
                  comparado com <b>{nomeDoMes(anterior)}</b> ({curto(anterior)})
                </>
              ) : null}
              .
            </>
          ) : analise.analise === 'mes_corrente' ? (
            <>
              <b>{capital(nomeDoMes(atual))} até ontem</b> ({curto(atual)}, {plural(atual.dias, 'dia', 'dias')})
              {anterior ? (
                <>
                  {' '}
                  comparado com os mesmos dias de <b>{nomeDoMes(anterior)}</b> ({curto(anterior)})
                </>
              ) : null}
              . Hoje fica de fora: está em andamento.
            </>
          ) : (
            <>
              {analise.analise === 'personalizado' ? (
                <><b>Período escolhido</b> ({atual.inicio} a {atual.fim})</>
              ) : <b>{curto(atual)}</b>}
              {anterior ? (
                <>
                  {' '}comparado com <b>{analise.analise === 'personalizado'
                    ? `${anterior.inicio} a ${anterior.fim}` : curto(anterior)}</b>
                </>
              ) : null}
              . {plural(atual.dias, 'dia', 'dias')}.
            </>
          )}
        </LinhaDoPeriodo>
        {!comparavel && <Aviso tom="atencao">{semComparacao}</Aviso>}
      </Flex>

      {/* ── Indicadores: uma faixa de cartões iguais ── */}
      <Box
        display="grid"
        gridTemplateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: `repeat(${indicadores}, minmax(0, 1fr))` }}
        sx={{ gap: '12px' }}
      >
        <Kpi
          rotulo="Venda"
          valor={reaisInteiros(a.venda)}
          titulo={reais(a.venda)}
          delta={comparavel ? v.venda_percentual : undefined}
          legenda={noAnterior(reaisCurtos(ind.anterior?.venda || 0))}
          explicacao="Faturado mais a faturar, sem pedidos cancelados."
        />
        <Kpi
          rotulo="Pedidos"
          valor={quantidade(a.pedidos)}
          delta={comparavel ? v.pedidos_percentual : undefined}
          legenda={noAnterior(quantidade(ind.anterior?.pedidos || 0))}
          explicacao="Os pedidos que somam a venda: faturados ou a faturar."
        />
        <Kpi
          rotulo="Ticket médio"
          valor={reaisInteiros(a.ticket_medio)}
          titulo={reais(a.ticket_medio)}
          delta={comparavel ? v.ticket_medio_percentual : undefined}
          legenda={noAnterior(reaisInteiros(ind.anterior?.ticket_medio || 0))}
          explicacao="O valor médio de cada pedido: a venda do período dividida pelo número de pedidos."
        />
        <Kpi
          rotulo="Clientes"
          valor={quantidade(a.clientes_atendidos)}
          delta={comparavel ? v.clientes_atendidos_percentual : undefined}
          legenda={noAnterior(quantidade(ind.anterior?.clientes_atendidos || 0))}
          explicacao="Quantos clientes diferentes compraram no período. Quem fez vários pedidos conta uma vez."
        />
        {mostraMargem && (
          <Kpi
            rotulo="Margem"
            valor={`${quantidade(a.margem_percentual!)}%`}
            delta={v.margem_pontos != null ? v.margem_pontos : undefined}
            sufixo=" p.p."
            legenda="ponderada pelo valor"
            explicacao="Quanto sobra da venda depois do custo dos produtos. Os pedidos maiores pesam mais."
          />
        )}
      </Box>

      {/* ── A venda dia a dia ao lado do que pede ação (mesma altura) ── */}
      <Grade alignItems="stretch">
        {porDia && (
          <CartaoComTitulo
            gridColumn={{ lg: 'span 8' }}
            titulo="Venda por dia"
            explicacao="Cada ponto é o total vendido no dia, sem os cancelados. A linha apagada mostra a comparação na mesma posição do intervalo (dia 1 com dia 1, dia 2 com dia 2). Os totais podem ter quantidades diferentes de dias úteis quando há feriado."
            legenda={
              <>
                {frase}
                {analise.investigavel && (
                  <>
                    {' '}
                    <BotaoDoRodape display="inline-flex" p={0} onClick={() => onInvestigar(pedido, `Por que a venda variou: ${curto(atual)}`)}>
                      <FiSearch size={13} aria-hidden="true" />
                      Por que variou?
                    </BotaoDoRodape>
                  </>
                )}
              </>
            }
            acoes={
              // Os totais dos dois períodos, no alto à direita: servem de legenda.
              // O do anterior aparece mesmo sem a linha dele (meses de tamanhos
              // diferentes não se sobrepõem dia a dia).
              <Flex sx={{ gap: '24px' }} wrap="wrap">
                {[
                  { nome: porDia.series[0]?.nome || 'Atual', total: a.venda, cor: corDaSerie('Atual', 0, cores), atual: true },
                  ...(comparavel && anterior && ind.anterior
                    ? [
                        {
                          nome: porDia.series[1]?.nome || `Anterior (${curto(anterior)})`,
                          total: ind.anterior.venda,
                          cor: porDia.series[1] ? corDaSerie(porDia.series[1].nome, 1, cores) : 'var(--pa-linha)',
                          atual: false,
                        },
                      ]
                    : []),
                ].map((p) => (
                  <Box key={p.nome}>
                    <Flex align="center" sx={{ gap: '6px' }} fontSize="12px" color="var(--pa-tinta-3)">
                      <Box as="i" w="8px" h="8px" borderRadius="50%" bg={p.cor} />
                      {p.nome}
                    </Flex>
                    <Flex align="center" sx={{ gap: '8px' }} mt="2px">
                      <Box fontSize="22px" lineHeight="28px" fontWeight={700} letterSpacing="-0.02em" title={reais(p.total)}>
                        {reaisCurtos(p.total)}
                      </Box>
                      {p.atual && comparavel && <Variacao valor={v.venda_percentual} />}
                    </Flex>
                  </Box>
                ))}
              </Flex>
            }
          >
            <Area unidade="reais" rotulos={porDia.rotulos} series={porDia.series} cores={cores} altura={280} pico="Maior venda" />
          </CartaoComTitulo>
        )}
        <PedeAcao
          gridColumn={{ lg: porDia ? 'span 4' : 'span 12' }}
          titulo="Pede sua ação"
          alertas={analise.alertas}
          fora={analise.alertas_fora_da_lista}
          resolvidos={analise.resolvidos_recentes}
          vazio="Nenhum alerta nesta análise."
          onAbrir={onAbrir}
          rodape={
            <BotaoDoRodape
              onClick={() =>
                onPerguntar(
                  // Os dois períodos na pergunta: o do mês não é "a mesma duração logo antes".
                  anterior
                    ? `Por que a venda de ${atual.inicio} a ${atual.fim} variou em relação a ${anterior.inicio} a ${anterior.fim}?`
                    : `Como foi a venda de ${atual.inicio} a ${atual.fim}?`,
                )
              }
            >
              <FiMessageCircle size={14} aria-hidden="true" />
              Perguntar ao assistente
            </BotaoDoRodape>
          }
        />
      </Grade>

      {/* ── O mapa: cada estado pelo indicador escolhido; clicar filtra a região ── */}
      {!!analise.por_estado?.length && (
        <CartaoComTitulo
          titulo="Mapa por estado"
          legenda="Cada estado é o do endereço do cliente (o hospital), não o do vendedor. Passe o mouse num estado para ver todos os números dele."
          explicacao="A venda soma o valor dos pedidos que viraram venda; nas cirurgias, o pedido leva o kit inteiro, então pode ficar acima do faturado. Eletrofisiologia e Tratamento de Feridas são linhas de negócio, não lugares: os clientes delas aparecem no estado de cada um; para ver só uma linha, use o filtro de região no alto."
          corpo={{ px: 0, pb: 0 }}
        >
          <MapaDasRegioes
            estados={analise.por_estado}
            comparavel={!!anterior}
            regioes={regioes}
            regiao={regiao}
            onEscolherRegiao={onEscolherRegiao}
            periodo={curto(atual)}
            anterior={anterior ? curto(anterior) : undefined}
            dias={atual.dias}
          />
        </CartaoComTitulo>
      )}

      {/* ── De onde veio a venda: família, vendedor e cliente ── */}
      {cartoesDeBaixo > 0 && (
        <Grade alignItems="stretch">
          {familias && (
            <CartaoComTitulo
              gridColumn={largura}
              titulo="Venda por família"
              legenda="A parte de cada família de produto na venda."
              acoes={
                familias.linhas[0] && (
                  <Box textAlign="right">
                    <Box fontSize="22px" lineHeight="28px" fontWeight={700}>
                      {quantidade(Number(familias.linhas[0].participacao_percentual) || 0)}%
                    </Box>
                    <Box fontSize="12px" color="var(--pa-tinta-3)" maxW="140px" noOfLines={1} title={String(familias.linhas[0].grupo)}>
                      {String(familias.linhas[0].grupo)}
                    </Box>
                  </Box>
                )
              }
            >
              <Colunas
                partes={familias.linhas.slice(0, 6).map((l) => ({
                  nome: String(l.grupo),
                  valor: Number(l.participacao_percentual) || 0,
                  texto: `${quantidade(Number(l.participacao_percentual) || 0)}%`,
                }))}
              />
            </CartaoComTitulo>
          )}
          {composicao?.vendedores && (
            <CartaoComTitulo gridColumn={largura} titulo="Vendedores" legenda="A parte de cada um na venda do período.">
              <BarraDividida
                cores={cores}
                partes={composicao.vendedores.maiores.map((l) => ({
                  nome: l.vendedor,
                  valor: l.participacao_percentual,
                  texto: `${quantidade(l.participacao_percentual)}%`,
                }))}
                outros={
                  composicao.vendedores.outros
                    ? {
                        nome: 'Outros',
                        valor: composicao.vendedores.outros.participacao_percentual,
                        texto: `${quantidade(composicao.vendedores.outros.participacao_percentual)}%`,
                      }
                    : undefined
                }
                todos={composicao.vendedores.todos?.map((l) => ({
                  nome: l.vendedor,
                  valor: l.participacao_percentual,
                  texto: `${quantidade(l.participacao_percentual)}%`,
                }))}
              />
            </CartaoComTitulo>
          )}
          {clientes && (
            <CartaoComTitulo gridColumn={largura} titulo="Maiores clientes" legenda="Quanto da venda está nos que mais compraram.">
              {composicao?.clientes && (
                <Medidor
                  rotulo={`Os ${composicao.clientes.maiores} maiores clientes`}
                  percentual={composicao.clientes.participacao_percentual}
                  valor={`${quantidade(composicao.clientes.participacao_percentual)}%`}
                  legenda={`da venda nos ${composicao.clientes.maiores} maiores`}
                />
              )}
              <ListaDeClientes linhas={clientes.linhas} />
            </CartaoComTitulo>
          )}
        </Grade>
      )}

      {/* ── O resto, só quando se pede ── */}
      {(!!analise.tabelas?.length || calor || sobre.length > 0) && (
        <Flex direction="column" sx={{ gap: '4px' }} borderTop="1px solid var(--pa-linha)" pt="8px">
          {(!!analise.tabelas?.length || calor) && (
            <Recolhido rotulo="Ver detalhamento: horários e tabelas" inicialmenteAberto>
              <Flex direction="column" sx={{ gap: '20px' }}>
                {calor && (
                  <CartaoComTitulo
                    titulo="Quando os pedidos entram"
                    explicacao="Cada linha é um dia da semana e cada coluna, uma hora. Quanto mais forte a cor, mais pedidos; o número fica no horário de pico. Pedido digitado no ERP sem hora fica de fora."
                  >
                    <Calor rotulos={calor.rotulos} series={calor.series} unidade={calor.unidade} escalaDe="Menos pedidos" escalaAte="Mais pedidos" />
                  </CartaoComTitulo>
                )}
                {!!analise.tabelas?.length && (
                  <TabelasDaAnalise tabelas={analise.tabelas} arquivo={arquivo} />
                )}
              </Flex>
            </Recolhido>
          )}
          {sobre.length > 0 && (
            <Recolhido rotulo={`Sobre os dados · ${sobre.length}`}>
              <Box as="ul" m={0} pl="22px" fontSize="13px" lineHeight="21px" color="var(--pa-tinta-2)">
                {sobre.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </Box>
            </Recolhido>
          )}
        </Flex>
      )}
    </Flex>
  );
}
